import { Formik } from "formik";
import { useId } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@volca/ui/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@volca/ui/components/ui/input-group";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { currencySymbol, useReportCurrency } from "@/profitability/currency";
import { reportError } from "@/profitability/utils";
import { submitSettingsForm } from "./submit-settings-form";
import type { GoalTolerance } from "./types";
import { fromInput, toInput, toleranceLabel } from "./values";

export const ToleranceField = ({
  tolerance,
  editable,
  onSave,
  onCancel,
}: {
  tolerance: GoalTolerance;
  editable: boolean;
  onSave: (tolerance: Partial<GoalTolerance>) => Promise<void>;
  onCancel: () => void;
}) => {
  const id = useId();
  const currency = useReportCurrency();
  if (!editable) return <Text>{toleranceLabel(tolerance, currency)}</Text>;
  return (
    <Formik
      initialValues={{
        amount: String(tolerance.amount),
        percent: toInput("percent", tolerance.percent),
      }}
      validateOnChange={false}
      validateOnBlur={false}
      validate={(values) => {
        const amount = fromInput("money", values.amount);
        const percent = fromInput("percent", values.percent);
        return {
          ...(!amount.ok || typeof amount.value !== "number" || amount.value < 0
            ? { amount: "Enter an amount of 0 or more" }
            : {}),
          ...(!percent.ok ? { percent: percent.error } : {}),
        };
      }}
      onSubmit={async (values, helpers) => {
        const amount = fromInput("money", values.amount);
        const percent = fromInput("percent", values.percent);
        if (!amount.ok || typeof amount.value !== "number" || !percent.ok)
          return;
        helpers.setStatus(undefined);
        try {
          await onSave({
            amount: amount.value,
            percent: typeof percent.value === "number" ? percent.value : null,
          });
          onCancel();
        } catch (error) {
          helpers.setStatus(
            `Could not save. ${reportError(error)} Your changes are still here. Try again.`,
          );
        }
      }}
    >
      {({
        values,
        errors,
        status,
        isSubmitting,
        submitForm,
        setFieldValue,
        setFieldError,
      }) => (
        <form onSubmit={(event) => submitSettingsForm(event, submitForm)}>
          <Stack gap={5}>
            {(["amount", "percent"] as const).map((key) => (
              <Field key={key} data-invalid={!!errors[key] || undefined}>
                <FieldLabel htmlFor={`${id}-${key}`}>
                  {key === "amount" ? "Amount" : "Percentage (optional)"}
                </FieldLabel>
                <InputGroup>
                  {key === "amount" && (
                    <InputGroupAddon>
                      <InputGroupText>
                        {currencySymbol(currency)}
                      </InputGroupText>
                    </InputGroupAddon>
                  )}
                  <InputGroupInput
                    id={`${id}-${key}`}
                    inputMode="decimal"
                    value={values[key]}
                    disabled={isSubmitting}
                    aria-invalid={!!errors[key] || undefined}
                    aria-describedby={`${id}-${key}-hint${errors[key] ? ` ${id}-${key}-error` : ""}`}
                    onChange={(event) => {
                      void setFieldValue(key, event.target.value);
                      setFieldError(key, undefined);
                    }}
                  />
                  {key === "percent" && (
                    <InputGroupAddon align="inline-end">
                      <InputGroupText>%</InputGroupText>
                    </InputGroupAddon>
                  )}
                </InputGroup>
                <FieldDescription id={`${id}-${key}-hint`}>
                  {key === "amount"
                    ? "Maximum difference from a reference figure."
                    : "If set, a figure also matches when its percentage difference is within this limit."}
                </FieldDescription>
                {errors[key] && (
                  <FieldError id={`${id}-${key}-error`}>
                    {errors[key]}
                  </FieldError>
                )}
              </Field>
            ))}
            {typeof status === "string" && (
              <Text role="alert" tone="destructive">
                {status}
              </Text>
            )}
            <Stack direction="row" gap={2} justify="end">
              <Button
                type="button"
                variant="outline"
                disabled={isSubmitting}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting}>
                Save
              </Button>
            </Stack>
          </Stack>
        </form>
      )}
    </Formik>
  );
};
